import { Link, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { authClient } from '../lib/auth-client';
import GmailBanner from './GmailBanner';
import { useReviewCount } from '../pages/ReviewPage';
import { useInboxCount } from '../pages/DashboardPage';

export default function Layout({ children, fullWidth }: { children: React.ReactNode; fullWidth?: boolean }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: review } = useReviewCount();
  const pending = review?.pending ?? 0;
  const { data: inbox } = useInboxCount();
  const waiting = inbox?.waiting ?? 0;

  const handleSignOut = async () => {
    await authClient.signOut();
    queryClient.clear();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass shadow-ambient">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-6">
          <nav className="flex items-center gap-8">
            <Link to="/" className="display-tight text-xl font-semibold text-primary">
              VendorOps
            </Link>
            {/* This slot used to be left empty on the reasoning that the
                wordmark already routes to `/`, so a second nav element pointing
                at the same place bought nothing. The badge is what changes that:
                the count is information no wordmark can carry, and a planner who
                has to open a screen to learn whether it needs them is being
                asked to poll. First in the nav because it is the default route
                and the one they come back to. */}
            <Link
              to="/"
              className="label-caps flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              Inbox
              {/* Absent at zero rather than a "0". Caught up is the goal state
                  (PRD §F7), and a badge that is always lit stops being read. */}
              {waiting > 0 && (
                <span className="inline-flex min-w-4 items-center justify-center rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary-foreground">
                  {waiting}
                </span>
              )}
            </Link>
            <Link
              to="/weddings"
              className="label-caps text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              Weddings
            </Link>
            <Link
              to="/vendors"
              className="label-caps text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              Vendors
            </Link>
            {/* PRD §F5 wants the queue count visible globally — mail set aside
                and never surfaced is worse than not setting it aside at all.
                Read as: visible globally *whenever there is something to see*.
                An empty queue is the designed steady state (MatchHealthPanel
                targets a rate under 20%), so an always-present link would sit
                beside Weddings and Vendors advertising a screen that is
                usually blank. It appears when it has contents and not before;
                the dashboard's "Needs filing" section is the ambient surface. */}
            {pending > 0 && (
              <Link
                to="/review"
                className="label-caps flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                Needs filing
                <span className="inline-flex min-w-4 items-center justify-center rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary-foreground">
                  {pending}
                </span>
              </Link>
            )}
            <Link
              to="/templates"
              className="label-caps text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              Templates
            </Link>
            <Link
              to="/settings"
              className="label-caps text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              Settings
            </Link>
          </nav>
          <button
            onClick={handleSignOut}
            className="label-caps text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            Sign out
          </button>
        </div>
      </header>
      {/* Sync health lives above every screen: a planner who does not know sync
          stopped is trusting a system that is not watching (PRD §F4). The one
          exception is first run, which the banner declines for itself. */}
      <GmailBanner />
      <main className={fullWidth ? '' : 'mx-auto max-w-5xl px-6 py-10'}>{children}</main>
    </div>
  );
}
